import { useQuery } from '@apollo/client';
import { datadogRum } from '@datadog/browser-rum';
import { WindowLocation, redirectTo, useLocation } from '@reach/router';
import axios from 'axios';
import { navigate } from 'gatsby';
import { stringifyUrl } from 'query-string';
import React, { FC, useContext, useEffect, useState } from 'react';
import { useDeepCompareEffect } from 'react-use';
import styled from 'styled-components';

import BodyText from '@ui/atoms/BodyText';
import FolxLoader from '@ui/atoms/FolxLoader';
import Header from '@ui/atoms/Header';

import useMemberInfo, {
	IAuthenticatedMember,
	IFragment,
	SubscriptionInfo,
} from '@hooks/useMemberInfo';

import { confirmAppointment } from '@utils/cogsworthAppointments';
import {
	IBookedAppointmentInfo,
	useCogsworthBookingRedirect,
} from '@utils/cogsworthBookingRedirect';
import { ApiEndpoints } from '@utils/endpoints';
import { exists } from '@utils/general';
import { combineMemberFragments } from '@utils/graphql';

import { getAuthToken, useAuth } from '@providers/AuthProvider';
import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';

import { IAuthenticatedMemberWithUnscheduledAppointments } from '@folx-types/member';

import { membership, membershipName, queryName } from '@graphql/fragments/membership';
import { unscheduledAppointments as UNSCHEDULED_APPOINTMENTS } from '@graphql/fragments/unscheduledAppointments';
import currentMemberWithSubscriptionsQuery from '@graphql/queries/currentMemberWithSubscriptions.gql';

import theme from '@theme/FolxTheme';

const AppointmentBookedContainer = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	margin: ${theme.spacing.xxl} 0;
	${Header} {
		margin: ${theme.spacing.sm} 0;
	}
`;

const toLowercaseSubscription = (subType: string) => {
	switch (subType) {
		case 'HRT_T':
			return 't';
		case 'HRT_E':
			return 'e';
		case 'MH_THERAPY':
			return 'mht';
		case 'MH_MEDICATION':
			return 'mhm';
		default:
			return subType.toLowerCase();
	}
};

const validateSearchParams = (object: Object): object is IBookedAppointmentInfo => {
	return Object.keys(object).every((key) => ['id', 'folxService', 'rescheduled'].includes(key));
};

/* Theory of operation

	1. Each cogsworth business has a global redirect URL configured in CW admin UI. After an appointment is booked or rescheduled (but not after it's cancelled), the user is redirected to that URL. The redirect includes information about the appointment in query params. Our Cogsworth instances are configured to redirect the user to a BE URL (/demux/cogsworth) at the end of booking. The prod FE redirects to prod BE; all other FEs redirect to sandbox BE.
	2. When the BE receives this redirect, it inspects appointment information included with it to determine which frontend environment the user came from and redirects the user to that FE environment. In prod BE, this redirect is always to prod FE; in sandbox BE, this redirect can be any non-prod FE, depending on where the user came from
	3. This page here is the target of that second (BE -> FE) redirect
	4. This page then looks at the included appointment information and makes some BE calls to determine what the user was doing (which path they booked the appointment from) to decide where in the FE they need to end up, and redirects them there. Specifically, we call the backend to discern whether the user is booking a new appointment or rescheduling an existing one and determine the service for which they are booking (by looking at the information in the return URL).

	Some historical appointments' return URL is not formatted like http[s]://host/service, but instead has a different URL path and no explicit service type — for example, http://localhost:8000/app/appointment_managed. In these cases, we deduce service type from the URL path when possible. When users are coming in manually through a booking URL, we are unable to deduce the service type. As a result, there is always a set of cases when service type is unknown; these cases land at a handler that shows generic success copy.

	Of note, appointment types that require approval (which currently is VC) run into problems when they are not post-processed by the correct handler. Specifically, the handler is required to auto-confirm those appointments; if the correct handler does not run, the appointment will not be auto-confirmed and therefore will get garbage-collected. As a result, service type inference from legacy return URLs has to correctly identify VC appointments, and manually booked VC appointments also have to be manually approved.
	*/

const AppointmentBooked: FC = () => {
	const [redirectLoading, setRedirectLoading] = useState<boolean>(false);
	const [redirect, setRedirect] = useState<IRedirectInfo>();

	const { initialized, loggedIn } = useAuth();
	const location = useLocation();
	const cogsworthBookingInfo: IBookedAppointmentInfo | undefined =
		useCogsworthBookingRedirect(location);

	const unscheduledAppointmentsFragment: IFragment = {
		name: 'UnscheduledAppointments',
		node: UNSCHEDULED_APPOINTMENTS,
	};
	const { loading: memberLoading, memberInfo } =
		useMemberInfo<IAuthenticatedMemberWithUnscheduledAppointments>([
			unscheduledAppointmentsFragment,
		]);

	const { client: graphQLv2Client } = useContext(GraphQLv2Context);
	const {
		data: membershipData,
		loading: membershipLoading,
		error: membershipError,
	} = useQuery<IAuthenticatedMember<SubscriptionInfo>>(
		combineMemberFragments([membershipName], [membership], queryName),
		{
			client: graphQLv2Client,
			skip: !loggedIn,
		}
	);

	const unscheduledAppointments = memberInfo?.unscheduledAppointments;

	const {
		loading: currentMemberWithSubsLoading,
		error: currentMemberWithSubsError,
		data: currentMemberWithSubsData,
	} = useQuery(currentMemberWithSubscriptionsQuery, { skip: !loggedIn });

	useEffect(() => {
		if (initialized && !currentMemberWithSubsLoading && exists(currentMemberWithSubsError)) {
			setRedirect({ type: 'error', error: 'Error fetching member data' });
			return;
		}
	}, [initialized, currentMemberWithSubsLoading, currentMemberWithSubsError]);

	type IRedirectInfo =
		| {
				type: 'internal';
		  }
		| {
				type: 'external';
				destination: string;
		  }
		| {
				type: 'error';
				error: string;
		  };

	const getRescheduledRedirect = async (cogsworthId: string) => {
		await confirmAppointment(cogsworthId);
		return `/app/member/appointments/reschedule/${cogsworthId}`;
	};

	useDeepCompareEffect(() => {
		async function performRedirect(
			cogsworthBookingInfo: IBookedAppointmentInfo | undefined,
			location: WindowLocation
		) {
			try {
				if (!cogsworthBookingInfo) {
					// Should not happen; CW demuxer should always pass booking info in query params
					setRedirect({ type: 'error', error: 'Missing appointment info' });
					return;
				}

				// If we have more keys than specified in the IBookedAppointmentInfo interface
				// then something is wrong and we're probably leaking PII again
				if (!validateSearchParams(cogsworthBookingInfo)) {
					setRedirect({ type: 'error', error: 'Incorrect appointment info' });
					return;
				}

				const memberSubscriptions =
					currentMemberWithSubsData.currentMember.memberSubscriptions.nodes.map((apt: any) =>
						toLowercaseSubscription(apt.subscriptionType)
					);

				// Call the backend to determine if the user is rescheduling
				const authorizationToken = await getAuthToken();
				const rescheduled = cogsworthBookingInfo?.rescheduled ?? false;

				const {
					data: { apptData },
				} = await axios.get(
					stringifyUrl({
						url: `${ApiEndpoints.cogsworth}/get-appointment-valid/${cogsworthBookingInfo.id}`,
					}),
					{
						headers: {
							authorizationToken,
						},
						responseType: 'json',
					}
				);

				let isPastCancelledDate = false;

				const membershipSubscription = membershipData?.authenticatedMember.membershipSubscription;
				if (membershipSubscription?.cancelAt) {
					const apptDate = new Date(apptData.date);
					const cancelDate = new Date(membershipSubscription?.cancelAt);

					isPastCancelledDate = apptDate > cancelDate;
				}

				const isUnscheduled = unscheduledAppointments?.some(
					(appt) => appt.cogsworthId == cogsworthBookingInfo.id
				);

				// Figure out from the appointment metadata what service this appointment is for
				const { folxService: service } = cogsworthBookingInfo;
				if (!service) {
					console.log("Couldn't deduce service");
				}
				const hasSubForService = memberSubscriptions?.includes(service!);
				const hasFolxMembership = memberSubscriptions?.includes('membership');

				console.log('eval service', service);

				let intentDestination;

				if (isUnscheduled) {
					console.log('Appointment was previously unscheduled');
					const confirmation = await confirmAppointment(cogsworthBookingInfo.id);

					intentDestination = new URL(
						service === 'transfer'
							? `/app/flows/appointment_booking/transfer/appointment_booked`
							: service === 'vc'
							? // New VC
							  `/app/flows/vhc_sub/confirm`
							: service === 'e'
							? // New E
							  `/app/flows/hrt_e_sub/confirm`
							: service === 't'
							? // New T
							  `/app/flows/hrt_t_sub/confirm`
							: service === 'followup'
							? // Followup
							  `/app/flows/followup/confirm`
							: service === 'mhm' || service == 'mht' || service == 'mhl' || service == 'mh-letter'
							? // MH
							  `/app/flows/mh_sub/confirm`
							: // New other
							  '/app/appointment_scheduled',
						location.href
					);
					navigate(intentDestination.pathname, {
						state: {
							search: location.search,
							confirmation: confirmation ? 'SUCCESS' : 'FAILURE',
							cogsworthId: cogsworthBookingInfo.id,
							isPastCancelledDate,
							apptDate: apptData?.date,
						},
						replace: true,
					});
					setRedirect({
						type: 'internal',
					});
				} else {
					intentDestination = new URL(
						service === 'transfer'
							? `/app/flows/appointment_booking/transfer/appointment_booked`
							: rescheduled || hasSubForService
							? // All rescheduled, and initial visit bookings where member subscription is established
							  await getRescheduledRedirect(cogsworthBookingInfo.id)
							: service === 'inj'
							? // Injection Training
							  await getRescheduledRedirect(cogsworthBookingInfo.id)
							: service === 'vc'
							? // New VC
							  `/app/flows/vhc_sub/appointment_selection`
							: service === 'e'
							? // New E
							  `/app/flows/hrt_e_sub/appointment_selection`
							: service === 't'
							? // New T
							  `/app/flows/hrt_t_sub/appointment_selection`
							: service === 'followup'
							? // Followup
							  `/app/flows/followup/appointment_selection`
							: service === 'mhm' || service == 'mht' || service == 'mhl' || service == 'mh-letter'
							? // MH
							  `/app/flows/mh_sub/appointment_selection`
							: // New other
							  '/app/appointment_scheduled',
						location.href
					);
					navigate(intentDestination.pathname, {
						state: {
							cogsworthId: cogsworthBookingInfo.id,
							search: location.search,
							rescheduled,
							isPastCancelledDate,
							apptDate: apptData?.date,
						},
						replace: true,
					});
					setRedirect({
						type: 'internal',
					});
				}
			} catch (error: any) {
				console.log(error);
				setRedirect({
					type: 'error',
					error: error?.message ?? 'Unknown error',
				});
			}
		}

		if (
			!redirectLoading &&
			exists(currentMemberWithSubsData) &&
			exists(memberInfo) &&
			exists(membershipData)
		) {
			setRedirectLoading(true);
			performRedirect(cogsworthBookingInfo, location);
		}
	}, [
		redirectLoading,
		cogsworthBookingInfo,
		location,
		currentMemberWithSubsLoading,
		currentMemberWithSubsData,
		memberLoading,
		memberInfo,
		membershipLoading,
		membershipData,
	]);

	switch (redirect?.type) {
		case 'external': {
			redirectTo(redirect.destination);
			break;
		}

		case 'error': {
			datadogRum.addError(
				`Appointment booked (Cogsworth redirect) error: ${JSON.stringify({
					error: redirect.error,
				})}`
			);

			// clear out whatever bad params we from the url
			window.history.replaceState({ search: '' }, '', location.pathname);

			return (
				<AppointmentBookedContainer>
					<Header level="4">Woops! Something may have gone wrong.</Header>
					<BodyText>
						Check your email for an appointment confirmation from us. If you don’t see one in the
						next 10 minutes, please reach out to{' '}
						<a href="mailto:folxsupport@folxhealth.com">folxsupport@folxhealth.com</a>.
					</BodyText>
					<BodyText>Error: {redirect.error}.</BodyText>
				</AppointmentBookedContainer>
			);
		}
	}

	return (
		<AppointmentBookedContainer>
			<FolxLoader />
		</AppointmentBookedContainer>
	);
};

export default AppointmentBooked;
